import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as Plus } from '../../../assets/icons/plus.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import timezones from '../../../enum/AllTimeZoneList';
import currencyCodesList from '../../../enum/CurrencyCodesList';
import {
  PriceComponentInterface,
  AddTariffPayloadInterface,
} from '../../../interface/tariff.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAccountAllList } from '../../../redux/slices/AccountManagementSlice';
import {
  fetchAllConnectorRating,
  fetchConnectorScopList,
} from '../../../redux/slices/ConnectorRatingSlice';
import {
  fetchAllTariffs as fetchAllCustomTariffs,
  fetchTariffCounts as fetchCustomTariffCounts,
} from '../../../redux/slices/CustomTariffSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  fetchAllPartnerTariffs,
  fetchPartnerTariffCounts,
} from '../../../redux/slices/PartnerTariffSlice';
import { fetchAllTariffs, fetchTariffCounts } from '../../../redux/slices/TariffSlice';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomMultiDatePicker from '../../../shared-components/custom-date-range/CustomMultiDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomRadioButton from '../../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTimePicker from '../../../shared-components/custom-time-input/CustomTimePicker';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
import { changeDateFormat } from '../../../shared-functions/DataTime';
import { getMyAccountCustomId } from '../../../shared-functions/JWTDocodeFunction';
import {
  formatDateWithTimezone,
  convertTimeToUTC,
  convertUTCtoTimezoneDate,
  convertUTCtoTimezoneTime,
} from '../../../shared-functions/TimezoneConversions';
import penaltyManagementApi from '../../../utils/api/penalty-management';
import tariffApi from '../../../utils/api/tariff';
import { generate6DigitNumber } from '../../../utils/brandingUtils';

const DAYS_OF_WEEK = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];

/**
 * Edit-tariff component
 * @returns {any} Edit-tariff component
 */
function EditTariff() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const timeSlot = false;
  const { portalType } = usePortal();
  const { tariffId } = useParams<{ tariffId: string }>();
  const [searchParameters] = useSearchParams();
  const isPartner = searchParameters.get('isPartner') === 'true';

  const scopeLeveelOptions =
    portalType === PortalType.PARTNER
      ? [
          { label: 'Station', value: 'station' },
          { label: 'Geography', value: 'geography' },
        ]
      : [
          { label: 'Partner', value: 'partner' },
          { label: 'Station', value: 'station' },
          { label: 'Geography', value: 'geography' },
        ];

  const { connectorRatingsAll, scopeList } = useAppSelector((state) => state.connectorRating);
  const { accountList } = useAppSelector((state) => state.accounts);
  const [loading, setLoading] = useState<boolean>(false);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [statusError, setStatusError] = useState<string>('');
  const [stationsList, setStationsList] = useState<Array<{ label: string; value: string }>>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [priceComponentErrors, setPriceComponentErrors] = useState<
    Record<number, { name?: string; value?: string }>
  >({});
  const [timeSlotErrors, setTimeSlotErrors] = useState<
    Record<
      number,
      {
        name?: string;
        startTime?: string;
        endTime?: string;
        recurrenceType?: string;
        priceMultiplier?: string;
        dates?: string;
        daysOfWeek?: string;
      }
    >
  >({});

  const [tariffDetails, setTariffDetails] = useState<AddTariffPayloadInterface>({
    status: true,
    enforcementDate: '',
    currency: '',
    timezone: '',
    expiryDate: '',
    tariffName: '',
    tariffType: '',
    currentType: '',
    connectorRating: '',
    stateChildType: 'State',
    cityChildType: 'City',
    scope: '',
    station: '',
    priceComponents: [
      { id: 1, type: 'Base amount', name: '', valueType: 'per-kWh', value: '', order: '0' },
      { id: 2, type: 'Operating amount', name: '', valueType: 'per-kWh', value: '', order: '1' },
    ],
    timeBasedPricingSlots: [],
  });

  const isExpiryRequired =
    portalType === PortalType.PARTNER || tariffDetails.tariffType === 'custom';

  /**
   * Get the scope name
   * @param {string} scope - Scope level (country, state, city)
   * @param {string} scopeId - Scope entity id
   * @returns {string} Scope display label or empty string
   */
  const getScopeName = (scope: string, scopeId: string) => {
    const currentScopeList: any[] = (scopeList as Record<string, any[]> | undefined)?.[scope] || [];
    return currentScopeList.find((scopeItem) => scopeItem.value === scopeId)?.label || '';
  };

  /**
   * Get the child type for a given scope and ID
   * @param {string} scope - The scope
   * @param {string} scopeId - The scope ID
   * @returns {string} The child type
   */
  const getChildType = (scope: string, scopeId: string) => {
    const currentScopeList: any[] = (scopeList as Record<string, any[]> | undefined)?.[scope] || [];
    return currentScopeList.find((scopeItem) => scopeItem.value === scopeId)?.childType || '';
  };
  /**
   * Get the partner and station name
   * @returns {string} The child type
   */
  const getPartnerStationName = (type: string, value: string): string => {
    let scopeName = '';
    if (type === 'PNR') {
      scopeName =
        accountList?.find((account) => account.accountCustomId === value)?.accountName || '';
    }
    if (type === 'STN') {
      scopeName = stationsList?.find((account) => String(account.value) === value)?.label || '';
    }
    return scopeName;
  };

  /**
   * Update tariff details field
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateTariffDetail = (
    field: string,
    value: string | number | boolean | PriceComponentInterface[] | null,
  ) => {
    setStatusError('');
    if (errors[field]) {
      setErrors((previous) => ({ ...previous, [field]: '' }));
    }
    const chargeData = {
      [field]: value,
    };
    switch (field) {
      // case 'currentType':
      //   dispatch(fetchAllConnectorRating(String(value)));
      //   break;
      case 'tariffType':
        chargeData.scope = null;
        chargeData.state = null;
        chargeData.city = null;
        chargeData.country = null;
        chargeData.station = null;
        chargeData.partner = null;
        chargeData.scopeId = null;
        chargeData.scopeName = null;
        if (value === 'global' && portalType !== PortalType.PARTNER) {
          setErrors((previous) => ({ ...previous, expiryDate: '' }));
        }
        break;
      case 'scope':
        if (value === 'geography') {
          chargeData.state = null;
          chargeData.city = null;
          chargeData.country = null;
          chargeData.station = null;
          chargeData.partner = null;
          chargeData.scopeId = null;
          chargeData.scopeName = null;
          dispatch(fetchConnectorScopList({ scope: 'country', scopeId: '' }));
        } else if (value !== 'geography' && portalType !== PortalType.PARTNER) {
          chargeData.state = null;
          chargeData.city = null;
          chargeData.country = null;
          chargeData.station = null;
          chargeData.partner = null;
          chargeData.scopeId = null;
          chargeData.scopeName = null;
          dispatch(fetchAccountAllList());
        } else {
          chargeData.state = null;
          chargeData.city = null;
          chargeData.country = null;
          chargeData.station = null;
          chargeData.partner = null;
          chargeData.scopeId = null;
          chargeData.scopeName = null;
          const accountCustomId = getMyAccountCustomId();
          penaltyManagementApi.findStationNames(accountCustomId).then((response) => {
            setStationsList(
              response.data.map((station: any) => ({ label: station.name, value: station._id })) ||
                [],
            );
          });
        }
        break;
      case 'country':
        dispatch(fetchConnectorScopList({ scope: 'state', scopeId: value }));
        chargeData.scopeId = value;
        chargeData.state = null;
        chargeData.city = null;
        chargeData.stateChildType = getChildType('country', String(value));
        chargeData.scopeName = getScopeName('country', String(value));
        break;
      case 'state':
        dispatch(fetchConnectorScopList({ scope: 'city', scopeId: value }));
        chargeData.scopeId = value;
        chargeData.city = null;
        chargeData.cityChildType = getChildType('state', String(value));
        chargeData.cityChildType = getScopeName('state', String(value));
        break;
      case 'city':
        chargeData.scopeId = value;
        chargeData.scopeName = getScopeName('city', String(value));
        break;
      case 'partner':
        chargeData.scopeId = generate6DigitNumber('PNR');
        chargeData.scopeName = getPartnerStationName('PNR', String(value));
        penaltyManagementApi.findStationNames(String(value)).then((response) => {
          setStationsList(
            response.data.map((station: any) => ({ label: station.name, value: station._id })) ||
              [],
          );
        });
        break;
      case 'station':
        chargeData.scopeId = generate6DigitNumber('STN');
        chargeData.scopeName = getPartnerStationName('STN', String(value));
        break;

      default:
        break;
    }

    setTariffDetails((previous) => ({ ...previous, ...chargeData }));
  };

  /**
   * Add price component to the price components list
   */
  const addPriceComponent = () => {
    updateTariffDetail('priceComponents', [
      ...tariffDetails.priceComponents,
      {
        id: Date.now(),
        type: '',
        name: '',
        valueType: 'per-kWh',
        value: '',
        order: String(tariffDetails.priceComponents.length),
      },
    ]);
  };
  /**
   * Check if a price component can be deleted.
   * Total length must be greater than 2, and deleting the component must leave
   * at least one Base Price component and at least one Operational Cost component.
   * @param {PriceComponentInterface} comp - The component to evaluate
   * @returns {boolean} True if deletion is allowed, false otherwise
   */
  const canDeletePriceComponent = (comp: PriceComponentInterface): boolean => {
    if (tariffDetails.priceComponents.length <= 2) return false;
    const typeString = comp.type ? comp.type.toLowerCase() : '';
    const isBase = typeString.includes('base');
    const isOps = typeString.includes('operat');

    if (isBase) {
      const baseCount = tariffDetails.priceComponents.filter((c) =>
        c.type ? c.type.toLowerCase().includes('base') : false,
      ).length;
      if (baseCount <= 1) return false;
    }
    if (isOps) {
      const opsCount = tariffDetails.priceComponents.filter((c) =>
        c.type ? c.type.toLowerCase().includes('operat') : false,
      ).length;
      if (opsCount <= 1) return false;
    }
    return true;
  };

  /**
   * Remove price component from the price components list
   * @param {number} id - Price component id
   * @returns {void}
   */
  const removePriceComponent = (id: number) => {
    const compTarget = tariffDetails.priceComponents.find((comp) => comp.id === id);
    if (!compTarget || !canDeletePriceComponent(compTarget)) return;
    updateTariffDetail(
      'priceComponents',
      tariffDetails.priceComponents.filter((comp) => comp.id !== id),
    );
  };

  /**
   * Update a specific field for a price component
   * @param {number} id - Price component id
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updatePriceComponent = (id: number, field: string, value: string | number) => {
    if ((field === 'name' || field === 'value') && priceComponentErrors[id]?.[field]) {
      setPriceComponentErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], [field]: '' },
      }));
    }
    if (field === 'type' && errors.priceComponents) {
      setErrors((previous) => ({ ...previous, priceComponents: '' }));
    }
    updateTariffDetail(
      'priceComponents',
      tariffDetails.priceComponents.map((comp) =>
        comp.id === id ? { ...comp, [field]: value } : comp,
      ),
    );
  };

  /**
   * Add a new empty time-based pricing slot
   */
  const addTimeSlot = () => {
    setTariffDetails((previous) => ({
      ...previous,
      timeBasedPricingSlots: [
        ...(previous.timeBasedPricingSlots || []),
        {
          id: Date.now(),
          name: '',
          startTime: '',
          endTime: '',
          recurrenceType: '',
          daysOfWeek: [],
          priceMultiplier: '1',
          date: '',
        },
      ],
    }));
  };

  /**
   * Remove a time-based pricing slot by id
   * @param {number} id - Slot id to remove
   */
  const removeTimeSlot = (id: number) => {
    setTimeSlotErrors((previous) => {
      const updated = { ...previous };
      delete updated[id];
      return updated;
    });
    setTariffDetails((previous) => ({
      ...previous,
      timeBasedPricingSlots: (previous.timeBasedPricingSlots || []).filter(
        (slot) => slot.id !== id,
      ),
    }));
  };

  /**
   * Update a field on a time-based pricing slot
   * @param {number} id - Slot id
   * @param {string} field - Field name to update
   * @param {any} value - New value
   */
  const updateTimeSlot = (id: number, field: string, value: string | string[]) => {
    if (timeSlotErrors[id]?.[field as keyof (typeof timeSlotErrors)[number]]) {
      setTimeSlotErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], [field]: '' },
      }));
    }
    if (field === 'recurrenceType' && timeSlotErrors[id]) {
      setTimeSlotErrors((previous) => ({
        ...previous,
        [id]: { ...previous[id], recurrenceType: '', dates: '', daysOfWeek: '' },
      }));
    }
    setTariffDetails((previous) => ({
      ...previous,
      timeBasedPricingSlots: (previous.timeBasedPricingSlots || []).map((slot) => {
        if (slot.id !== id) {
          return slot;
        }
        if (field === 'recurrenceType') {
          return {
            ...slot,
            recurrenceType: value as string,
            dates: value === 'non-recurring' ? slot.dates || [] : [],
            daysOfWeek: value === 'weekly' ? slot.daysOfWeek || [] : [],
          };
        }
        return { ...slot, [field]: value };
      }),
    }));
  };

  /**
   * Validate required tariff fields and populate inline field errors.
   * @returns {boolean} True when the form is valid, false otherwise
   */
  const validateTariffDetails = (): boolean => {
    const newErrors: Record<string, string> = {};
    const newPriceComponentErrors: Record<number, { name?: string; value?: string }> = {};
    const newTimeSlotErrors: Record<
      number,
      {
        name?: string;
        startTime?: string;
        endTime?: string;
        recurrenceType?: string;
        priceMultiplier?: string;
        dates?: string;
        daysOfWeek?: string;
      }
    > = {};

    if (!tariffDetails.tariffName) {
      newErrors.tariffName = 'Tariff Name is required';
    }
    if (!tariffDetails.currency) {
      newErrors.currency = 'Currency code is required';
    }
    if (!tariffDetails.timezone) {
      newErrors.timezone = 'Timezone is required';
    }
    if (!tariffDetails.tariffType) {
      newErrors.tariffType = 'Tariff Type is required';
    }
    if (tariffDetails.tariffType === 'custom') {
      if (
        tariffDetails.scope === 'partner' &&
        portalType !== PortalType.PARTNER &&
        !tariffDetails.partner
      ) {
        newErrors.partner = 'Partner is required';
      }
      if (tariffDetails.scope === 'station') {
        if (portalType !== PortalType.PARTNER && !tariffDetails.partner) {
          newErrors.partner = 'Partner is required';
        }
        if (!tariffDetails.station) {
          newErrors.station = 'Station is required';
        }
      }
      if (tariffDetails.scope === 'geography' && !tariffDetails.country) {
        newErrors.country = 'Country is required';
      }
      if (!tariffDetails.scope) {
        newErrors.scope = 'Scope is required';
      }
    }
    if (!tariffDetails.currentType) {
      newErrors.currentType = 'Current Type is required';
    }
    if (!tariffDetails.connectorRating) {
      newErrors.connectorRating = 'Connector Rating is required';
    }
    if (!tariffDetails.enforcementDate) {
      newErrors.enforcementDate = 'Enforcement Date is required';
    }
    if (isExpiryRequired && !tariffDetails.expiryDate) {
      newErrors.expiryDate = 'Expiry Date is required';
    }

    // Validate price components
    const hasBasePrice = tariffDetails.priceComponents.some(
      (comp) => comp.type && comp.type.toLowerCase().includes('base'),
    );
    const hasOperationalCost = tariffDetails.priceComponents.some(
      (comp) => comp.type && comp.type.toLowerCase().includes('operat'),
    );

    if (!hasBasePrice || !hasOperationalCost) {
      newErrors.priceComponents =
        'At least one Base Price and one Operational Cost component are required';
    }

    tariffDetails.priceComponents.forEach((comp) => {
      if (!comp.name) {
        newPriceComponentErrors[comp.id] = {
          ...newPriceComponentErrors[comp.id],
          name: 'Component Name is required',
        };
      }
      if (!comp.value) {
        newPriceComponentErrors[comp.id] = {
          ...newPriceComponentErrors[comp.id],
          value: 'Value is required',
        };
      }
    });

    // Validate time-based pricing slots if any exist
    if (tariffDetails.timeBasedPricingSlots && tariffDetails.timeBasedPricingSlots.length > 0) {
      tariffDetails.timeBasedPricingSlots.forEach((slot) => {
        const slotError: {
          name?: string;
          startTime?: string;
          endTime?: string;
          recurrenceType?: string;
          priceMultiplier?: string;
          dates?: string;
          daysOfWeek?: string;
        } = {};

        if (!slot.name || !slot.name.trim()) {
          slotError.name = 'Slot Name is required';
        }
        if (!slot.startTime) {
          slotError.startTime = 'Start Time is required';
        }
        if (!slot.endTime) {
          slotError.endTime = 'End Time is required';
        } else if (slot.startTime && slot.startTime >= slot.endTime) {
          slotError.endTime = 'End Time must be after Start Time';
        }
        if (!slot.recurrenceType) {
          slotError.recurrenceType = 'Recurrence Type is required';
        }
        if (!slot.priceMultiplier || String(slot.priceMultiplier).trim() === '') {
          slotError.priceMultiplier = 'Price Multiplier is required';
        }
        if (slot.recurrenceType === 'non-recurring' && (!slot.dates || slot.dates.length === 0)) {
          slotError.dates = 'Date is required';
        }
        if (
          slot.recurrenceType === 'weekly' &&
          (!slot.daysOfWeek || slot.daysOfWeek.length === 0)
        ) {
          slotError.daysOfWeek = 'Days of Week is required';
        }

        if (Object.keys(slotError).length > 0) {
          newTimeSlotErrors[slot.id] = slotError;
        }
      });
    }

    setErrors(newErrors);
    setPriceComponentErrors(newPriceComponentErrors);
    setTimeSlotErrors(newTimeSlotErrors);

    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(newPriceComponentErrors).length === 0 &&
      Object.keys(newTimeSlotErrors).length === 0
    );
  };

  /**
   * Handle save tariff
   */
  const handleSave = () => {
    if (!validateTariffDetails()) {
      return;
    }

    const scopeLevels = [];
    if (tariffDetails.country) {
      scopeLevels.push({
        scope: 'country',
        scopeId: tariffDetails.country,
        scopeName: getScopeName('country', tariffDetails.country),
      });
    }
    if (tariffDetails.state) {
      scopeLevels.push({
        scope: 'state',
        scopeId: tariffDetails.state,
        scopeName: getScopeName('state', tariffDetails.state),
      });
    }
    if (tariffDetails.city) {
      scopeLevels.push({
        scope: 'city',
        scopeId: tariffDetails.city,
        scopeName: getScopeName('city', tariffDetails.city),
      });
    }

    const request: any = {
      name: tariffDetails.tariffName,
      currency: tariffDetails.currency,
      type: tariffDetails.tariffType,
      currentType: tariffDetails.currentType,
      timezone: tariffDetails.timezone,
      effectiveFrom: tariffDetails.enforcementDate
        ? formatDateWithTimezone(tariffDetails.enforcementDate, tariffDetails.timezone)
        : null,
      isActive: tariffDetails.status,
      tariffPricingComponent: tariffDetails.priceComponents.map((price: any) => ({
        componentName: price.name,
        componentType: price.type,
        valueType: price.valueType,
        value: price.value,
        order: price.order,
      })),
    };

    if (
      tariffDetails.expiryDate &&
      formatDateWithTimezone(tariffDetails.expiryDate, tariffDetails.timezone)
    ) {
      request.effectiveTo = formatDateWithTimezone(
        tariffDetails.expiryDate,
        tariffDetails.timezone,
      );
    }

    if (tariffDetails.connectorRating) {
      const connectorRating: any = connectorRatingsAll.find(
        (rating: any) =>
          (rating._id || rating.ratingId || rating.id || rating.value) ===
          tariffDetails.connectorRating,
      );

      request.connectorRating = connectorRating
        ? {
            ratingId: connectorRating._id || connectorRating.ratingId || connectorRating.id,
            name: connectorRating.name || connectorRating.connectorRatingName || '',
            capacityKW: connectorRating.capacityKW,
            currentType: connectorRating.currentType,
            unit: connectorRating.unit,
          }
        : null;
    }

    if (tariffDetails.scope) {
      request.scope = tariffDetails.scope;
    }
    if (tariffDetails.scopeId) {
      request.scopeId = tariffDetails.scopeId;
    }
    if (tariffDetails.scopeName) {
      request.scopeName = tariffDetails.scopeName;
    }

    if (tariffDetails.tariffType === 'custom' && tariffDetails.partner) {
      request.accountCustomId = tariffDetails.partner;
    }
    if (portalType === PortalType.PARTNER) {
      request.accountCustomId = getMyAccountCustomId();
    }
    if (tariffDetails.tariffType === 'custom' && tariffDetails.station) {
      request.stationId = tariffDetails.station;
    }

    if (request.scope === 'geography' && scopeLevels.length > 0) {
      request.scopeLevels = scopeLevels;
    } else {
      request.scopeLevels = [];
    }

    if (tariffDetails.timeBasedPricingSlots && tariffDetails.timeBasedPricingSlots.length > 0) {
      request.isTimeBasedPricingEnabled = true;
      request.timeBasedPricingDetails = tariffDetails.timeBasedPricingSlots.map((slot) => {
        const slotDetail: {
          name: string;
          startTime: string;
          endTime: string;
          recurrenceType: string;
          priceMultiplier: number;
          daysOfWeek?: string[];
          dates?: string[];
        } = {
          name: slot.name,
          startTime: convertTimeToUTC(slot.startTime, tariffDetails.timezone),
          endTime: convertTimeToUTC(slot.endTime, tariffDetails.timezone),
          recurrenceType: slot.recurrenceType,
          priceMultiplier: Number(slot.priceMultiplier),
        };

        if (slot.recurrenceType === 'weekly' && slot.daysOfWeek && slot.daysOfWeek.length > 0) {
          slotDetail.daysOfWeek = slot.daysOfWeek;
        } else if (
          slot.recurrenceType === 'non-recurring' &&
          Array.isArray(slot.dates) &&
          slot.dates.length > 0
        ) {
          slotDetail.dates = slot.dates;
        }

        return slotDetail;
      });
    }

    setLoading(true);
    setStatusError('');
    tariffApi
      .editTariff(String(tariffId), request, isPartner)
      .then(() => {
        setLoading(false);
        setOpenStatus(true);
      })
      .catch((error: any) => {
        setLoading(false);
        const errorMessage = error?.response?.data?.message || 'Failed to edit tariff';
        dispatch(
          setNotificationMessage({
            status: true,
            message: errorMessage,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
        const lowerMessage = errorMessage.toLowerCase();
        if (
          lowerMessage.includes('deactivat') ||
          lowerMessage.includes('status') ||
          lowerMessage.includes('inactive') ||
          lowerMessage.includes('active')
        ) {
          setStatusError(errorMessage);
        } else {
          setStatusError('');
        }
      });
  };

  useEffect(() => {
    if (connectorRatingsAll.length === 0) {
      dispatch(fetchAllConnectorRating());
    }
    if (tariffId) {
      tariffApi.fetchSingleTariff(tariffId, isPartner).then((response) => {
        const connectorRatingId =
          typeof response?.data?.connectorRating === 'string'
            ? response?.data?.connectorRating
            : response?.data?.connectorRating?.ratingId ||
              response?.data?.connectorRating?._id ||
              response?.data?.connectorRating?.id ||
              response?.data?.ratingId ||
              '';

        const output = {
          status: response?.data?.isActive,
          enforcementDate: convertUTCtoTimezoneDate(
            response?.data?.effectiveFrom,
            response?.data?.timezone,
          ),
          expiryDate: convertUTCtoTimezoneDate(
            response?.data?.effectiveTo,
            response?.data?.timezone,
          ),
          tariffName: response?.data?.name,
          currency: response?.data?.currency,
          timezone: response?.data?.timezone,
          tariffType: response?.data?.type,
          partner: response?.data?.accountCustomId,
          station: response?.data?.stationId,
          currentType:
            response?.data?.currentType || response?.data?.connectorRating?.currentType || '',
          connectorRating: connectorRatingId,
          country: response?.data?.scopeLevels?.find((level: any) => level.scope === 'country')
            ?.scopeId,
          state: response?.data?.scopeLevels?.find((level: any) => level.scope === 'state')
            ?.scopeId,
          city: response?.data?.scopeLevels?.find((level: any) => level.scope === 'city')?.scopeId,
          scope: response?.data?.scope,
          scopeId: response?.data?.scopeId,
          scopeName: response?.data?.scopeName,
          stateChildType: 'State',
          cityChildType: 'City',
          priceComponents: response?.data?.tariffPricingComponent.map(
            (comp: any, index: number) => ({
              id: index + 1,
              type: comp.componentType,
              name: comp.componentName,
              valueType: comp.valueType,
              value: comp.value,
              order: comp.order,
            }),
          ),
          timeBasedPricingSlots: (response?.data?.timeBasedPricingDetails || []).map(
            (slot: any, index: number) => ({
              id: index + 1,
              name: slot.name,
              startTime: convertUTCtoTimezoneTime(slot.startTime, response?.data?.timezone),
              endTime: convertUTCtoTimezoneTime(slot.endTime, response?.data?.timezone),
              recurrenceType: slot.recurrenceType,
              daysOfWeek: slot.daysOfWeek || [],
              priceMultiplier: String(slot.priceMultiplier),
              dates: slot.dates?.map((date: string) => ConvertDDMMYYYYtoISO(date)) || [],
            }),
          ),
        };
        setTariffDetails(output);
        if (output.tariffType === 'custom') {
          dispatch(fetchConnectorScopList({ scope: 'country', scopeId: '' }));
          dispatch(fetchConnectorScopList({ scope: 'state', scopeId: output.country }));
          dispatch(fetchConnectorScopList({ scope: 'city', scopeId: output.state }));
          dispatch(fetchAccountAllList());
        }
        if (output.partner) {
          penaltyManagementApi.findStationNames(String(output.partner)).then((response1) => {
            setStationsList(
              response1.data.map((station: any) => ({ label: station.name, value: station._id })) ||
                [],
            );
          });
        }
      });
    }
  }, [tariffId, dispatch, isPartner]);
  useEffect(() => {
    const chargeData: any = {};
    if (
      (scopeList as { country?: Array<{ label: string; value: string }> })?.country &&
      tariffDetails.country
    ) {
      chargeData.stateChildType = getChildType('country', String(tariffDetails.country));
    }
    if (
      (scopeList as { state?: Array<{ label: string; value: string }> })?.state &&
      tariffDetails.state
    ) {
      chargeData.cityChildType = getChildType('state', String(tariffDetails.state));
    }
    if (Object.keys(chargeData).length > 0) {
      setTariffDetails((previous) => ({ ...previous, ...chargeData }));
    }
  }, [scopeList]);

  return (
    <div className="add-tariff-wrapper">
      <ModalBox
        open={openStatus}
        title=""
        content={
          <SuccessNotification
            title="Charging Tariff Has Been Updated Successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setOpenStatus(false);
              if (isPartner) {
                dispatch(fetchAllPartnerTariffs({ page: 1, limit: 20, partner: true } as any));
                dispatch(fetchPartnerTariffCounts('partner'));
              } else {
                if (tariffDetails.tariffType === 'global') {
                  dispatch(fetchAllTariffs({ page: 1, limit: 20, type: 'global' }));
                  dispatch(fetchTariffCounts('global'));
                }
                if (tariffDetails.tariffType === 'custom') {
                  dispatch(fetchAllCustomTariffs({ page: 1, limit: 20, type: 'custom' }));
                  dispatch(fetchCustomTariffCounts('custom'));
                }
              }
              navigate('/ev-admin/tariffs');
            }}
          />
        }
        closeModal={() => setOpenStatus(false)}
        header={false}
        rightCorner=""
        notification
      />
      <PageTitle
        title="Edit Charging Tariffs"
        topRightContent={
          <div className="page-title-button-group">
            <MyButton
              label="Cancel"
              buttonType="outline"
              onClickFunc={() => navigate('/ev-admin/tariffs')}
            />

            <MyButton
              label="Save"
              buttonType="submit"
              disableButton={loading}
              loading={loading}
              onClickFunc={handleSave}
            />
          </div>
        }
      />

      <div className="add-tariff-form-card">
        <div className="form-section">
          <p className="section-title">Basic Details</p>
          <div className="basic-details-grid">
            <CustomInput
              inputLabel="Tariff Name"
              placeholder="Enter Tariff Name"
              name="tariffName"
              type="text"
              value={tariffDetails.tariffName}
              onChangeValue={(name, value) => updateTariffDetail('tariffName', value)}
              valueRequired
              error={!!errors.tariffName}
              errorMessage={errors.tariffName}
            />
            <SelectField
              inputLabel="Time Zone"
              options={timezones}
              name="timezone"
              selectedValue={tariffDetails.timezone}
              placeholder="Choose Time Zone"
              onChangeValue={(name, value) => updateTariffDetail('timezone', value)}
              valueRequired
              error={!!errors.timezone}
              errorMessage={errors.timezone}
            />
            <SelectField
              inputLabel="Currency Code"
              options={currencyCodesList}
              name="currency"
              selectedValue={tariffDetails.currency}
              placeholder="Choose Currency code"
              onChangeValue={(name, value) => updateTariffDetail('currency', value)}
              valueRequired
              error={!!errors.currency}
              errorMessage={errors.currency}
            />
            <SelectField
              inputLabel="Tariff Type"
              options={[
                { label: 'Custom', value: 'custom' },
                { label: 'Global', value: 'global' },
              ]}
              name="tariffType"
              selectedValue={tariffDetails.tariffType}
              placeholder="Choose Tariff Type"
              onChangeValue={(name, value) => updateTariffDetail('tariffType', value)}
              valueRequired
              error={!!errors.tariffType}
              errorMessage={errors.tariffType}
            />
            {tariffDetails.tariffType === 'custom' && (
              <SelectField
                inputLabel="Scope Level"
                options={scopeLeveelOptions}
                name="scope"
                selectedValue={(tariffDetails as any).scope || ''}
                placeholder="Choose Scope Level"
                onChangeValue={(name, value) => updateTariffDetail('scope', value)}
                valueRequired
                error={!!errors.scope}
                errorMessage={errors.scope}
              />
            )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope &&
              portalType !== PortalType.PARTNER &&
              tariffDetails.scope !== 'geography' && (
                <SelectField
                  inputLabel="Partner"
                  options={
                    accountList?.map((account) => ({
                      label: account.accountName,
                      value: account.accountCustomId,
                    })) || []
                  }
                  name="partner"
                  selectedValue={tariffDetails.partner || ''}
                  onChangeValue={(name, value) => updateTariffDetail('partner', value)}
                  placeholder="Choose Partner"
                  valueRequired
                  error={!!errors.partner}
                  errorMessage={errors.partner}
                />
              )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope &&
              tariffDetails.scope === 'station' && (
                <SelectField
                  inputLabel="Station"
                  options={stationsList || []}
                  name="station"
                  selectedValue={tariffDetails.station || ''}
                  onChangeValue={(name, value) => updateTariffDetail('station', value)}
                  placeholder="Choose Station"
                  valueRequired
                  error={!!errors.station}
                  errorMessage={errors.station}
                />
              )}
            {tariffDetails.tariffType === 'custom' && tariffDetails.scope === 'geography' && (
              <SelectField
                inputLabel="Country"
                options={
                  (scopeList as { country?: Array<{ label: string; value: string }> })?.country ||
                  []
                }
                name="country"
                selectedValue={(tariffDetails as any).country || ''}
                onChangeValue={(name, value) => updateTariffDetail('country', value)}
                placeholder="Choose Country"
                valueRequired
                error={!!errors.country}
                errorMessage={errors.country}
              />
            )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope === 'geography' &&
              tariffDetails.country && (
                <SelectField
                  inputLabel={tariffDetails.stateChildType}
                  options={
                    (scopeList as { state?: Array<{ label: string; value: string }> })?.state || []
                  }
                  name="state"
                  selectedValue={(tariffDetails as any).state || ''}
                  onChangeValue={(name, value) => updateTariffDetail('state', value)}
                  placeholder="Choose State"
                />
              )}
            {tariffDetails.tariffType === 'custom' &&
              tariffDetails.scope === 'geography' &&
              tariffDetails.country &&
              tariffDetails.state && (
                <SelectField
                  inputLabel={tariffDetails.cityChildType}
                  options={
                    (scopeList as { city?: Array<{ label: string; value: string }> })?.city || []
                  }
                  name="city"
                  selectedValue={(tariffDetails as any).city || ''}
                  onChangeValue={(name, value) => updateTariffDetail('city', value)}
                  placeholder="Choose City"
                />
              )}
            {tariffDetails.tariffType === 'custom' && (
              <CustomInput
                inputLabel="Scope Id"
                placeholder="Scope Id (Auto Generated)"
                name="scopeId"
                type="text"
                value={tariffDetails.scopeId || ''}
                readOnly
                onChangeValue={(name, value) => updateTariffDetail('scopeId', value)}
              />
            )}
            {tariffDetails.tariffType === 'custom' && (
              <CustomInput
                inputLabel="Scope Name"
                placeholder="Scope Name"
                name="scopeName"
                value={tariffDetails.scopeName || ''}
                type="text"
                onChangeValue={(name, value) => updateTariffDetail('scopeName', value)}
              />
            )}
          </div>
        </div>

        <div className="form-section">
          <p className="section-title">Technical Details</p>

          <div className="technical-details-grid">
            <SelectField
              inputLabel="Current Type"
              options={[
                {
                  label: 'AC',
                  value: 'AC',
                },
                {
                  label: 'DC',
                  value: 'DC',
                },
              ]}
              placeholder="Choose Current Type"
              selectedValue={tariffDetails.currentType}
              name="currentType"
              onChangeValue={(name, value) => updateTariffDetail('currentType', value)}
              valueRequired
              error={!!errors.currentType}
              errorMessage={errors.currentType}
            />
            <SelectField
              inputLabel="Connector Rating (kW)"
              options={connectorRatingsAll
                .filter(
                  (rating: any) =>
                    !tariffDetails.currentType ||
                    String(rating.currentType || '').toLowerCase() ===
                      String(tariffDetails.currentType || '').toLowerCase(),
                )
                .map((rating: any) => ({
                  label:
                    rating.label ||
                    `${rating.currentType || ''} ${rating.capacityKW || ''} ${rating.unit || 'KW'}`.trim(),
                  value: rating._id || rating.ratingId || rating.id || rating.value,
                }))}
              placeholder="Choose Connector Rating"
              selectedValue={tariffDetails.connectorRating}
              name="connectorRating"
              onChangeValue={(name, value) => updateTariffDetail('connectorRating', value)}
              valueRequired
              error={!!errors.connectorRating}
              errorMessage={errors.connectorRating}
            />
            <CustomDatePicker
              inputLabel="Enforcement Date"
              placeholder="DD/MM/YYYY 00:00:00"
              requiredTime
              name="enforcementDate"
              value={tariffDetails.enforcementDate}
              onChangeValue={(name, value) => updateTariffDetail('enforcementDate', value)}
              valueRequired
              error={!!errors.enforcementDate}
              errorMessage={errors.enforcementDate}
              minDate={changeDateFormat(new Date().toISOString())}
            />
            <CustomDatePicker
              inputLabel={isExpiryRequired ? 'Expiry Date' : 'Expiry Date (Optional)'}
              placeholder="DD/MM/YYYY 00:00:00"
              requiredTime
              name="expiryDate"
              value={tariffDetails.expiryDate}
              onChangeValue={(name, value) => updateTariffDetail('expiryDate', value)}
              valueRequired={isExpiryRequired}
              error={!!errors.expiryDate}
              errorMessage={errors.expiryDate}
              minDate={changeDateFormat(new Date().toISOString())}
            />
          </div>

          <div className="status-section">
            <p className="status-label">Status</p>
            <div className="status-radio-group">
              <CustomRadioButton
                name="status"
                label="Active"
                value="Active"
                selected={tariffDetails.status === true}
                onChangeFunc={(name, value) => updateTariffDetail('status', value === 'Active')}
              />
              <CustomRadioButton
                name="status"
                label="Inactive"
                value="Inactive"
                selected={tariffDetails.status === false}
                onChangeFunc={(name, value) => updateTariffDetail('status', value === 'Active')}
              />
            </div>
            {(errors.status || statusError) && (
              <p
                className="custom-input-error-message"
                style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                {errors.status || statusError}
              </p>
            )}
          </div>
        </div>

        <div className="form-section price-components-section">
          <div className="section-header-flex">
            <p className="section-title">Price Components</p>
            <MyButton
              label="Add Components"
              buttonType="primary-outline"
              svgIcon={<Plus />}
              onClickFunc={addPriceComponent}
            />
          </div>
          {errors.priceComponents && (
            <p
              className="error-message"
              style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              {errors.priceComponents}
            </p>
          )}

          <div className="price-components-list">
            {tariffDetails.priceComponents.map((comp) => (
              <div key={comp.id} className="price-component-grid">
                <SelectField
                  inputLabel="Component Type"
                  options={[
                    { label: 'Base Price', value: 'Base price' },
                    { label: 'Operating Cost', value: 'Operational cost' },
                  ]}
                  name={`type-${comp.id}`}
                  placeholder="Select Component Type"
                  selectedValue={comp.type}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'type', value)}
                />
                <CustomInput
                  inputLabel="Component Name"
                  placeholder="Enter Component Name"
                  name={`name-${comp.id}`}
                  type="text"
                  value={comp.name}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'name', value)}
                  valueRequired
                  error={!!priceComponentErrors[comp.id]?.name}
                  errorMessage={priceComponentErrors[comp.id]?.name}
                />
                <SelectField
                  inputLabel="Value Typoe"
                  options={[
                    { label: 'Per kWh', value: 'per-kWh' },
                    { label: 'Per %', value: 'percentage' },
                  ]}
                  name={`valueType-${comp.id}`}
                  placeholder="Select Value Type"
                  selectedValue={comp.valueType}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'valueType', value)}
                />
                <CustomInput
                  inputLabel={
                    tariffDetails.currency ? `Value (${tariffDetails.currency})` : 'Value'
                  }
                  placeholder="Enter Value"
                  suffixText={tariffDetails.currency || ''}
                  name={`value-${comp.id}`}
                  type="number"
                  value={comp.value}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'value', value)}
                  decimalLimit={2}
                  maxValue={1000}
                  valueRequired
                  error={!!priceComponentErrors[comp.id]?.value}
                  errorMessage={priceComponentErrors[comp.id]?.value}
                />
                <CustomInput
                  inputLabel="Order"
                  placeholder="Order"
                  name={`order-${comp.id}`}
                  type="number"
                  value={comp.order}
                  onChangeValue={(name, value) => updatePriceComponent(comp.id, 'order', value)}
                  decimalLimit={0}
                  maxValue={100}
                />
                {canDeletePriceComponent(comp) && (
                  <div
                    className="delete-action-button"
                    role="presentation"
                    onClick={() => removePriceComponent(comp.id)}>
                    <DeleteIcon width="16" height="16" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {timeSlot && (
          <div className="form-section time-based-pricing-section">
            <div className="section-header-flex">
              <p className="section-title">Time Based Pricing Slots</p>
              <MyButton
                label="Add Time Slot"
                buttonType="primary-outline"
                svgIcon={<Plus />}
                onClickFunc={addTimeSlot}
              />
            </div>

            {tariffDetails.timeBasedPricingSlots &&
              tariffDetails.timeBasedPricingSlots.length > 0 && (
                <div className="time-slots-list">
                  {/* <div className="time-slot-header-row">
                  <span>Slot Name</span>
                  <span>Start Time</span>
                  <span>End Time</span>
                  <span>Date</span>
                  <span>Days</span>
                  <span>Multiplier</span>
                  <span />
                </div> */}
                  {tariffDetails.timeBasedPricingSlots.map((slot) => (
                    <div key={slot.id} className="time-slot-grid">
                      <CustomInput
                        inputLabel="Slot Name"
                        name={`slotName-${slot.id}`}
                        type="text"
                        value={slot.name}
                        placeholder="Enter Slot Name"
                        onChangeValue={(name, value) =>
                          updateTimeSlot(slot.id, 'name', String(value))
                        }
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.name}
                        errorMessage={timeSlotErrors[slot.id]?.name}
                      />
                      <CustomTimePicker
                        inputLabel="Start Time"
                        name={`startTime-${slot.id}`}
                        value={slot.startTime}
                        placeholder="00:00"
                        onChangeValue={(name, value) => updateTimeSlot(slot.id, 'startTime', value)}
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.startTime}
                        errorMessage={timeSlotErrors[slot.id]?.startTime}
                      />
                      <CustomTimePicker
                        inputLabel="End Time"
                        name={`endTime-${slot.id}`}
                        value={slot.endTime}
                        placeholder="00:00"
                        onChangeValue={(name, value) => updateTimeSlot(slot.id, 'endTime', value)}
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.endTime}
                        errorMessage={timeSlotErrors[slot.id]?.endTime}
                      />
                      <SelectField
                        inputLabel="Recurrence Type"
                        options={[
                          { label: 'Daily', value: 'daily' },
                          { label: 'Weekly', value: 'weekly' },
                          { label: 'Non Recurring', value: 'non-recurring' },
                        ]}
                        name={`recurrenceType-${slot.id}`}
                        placeholder="Select Recurrence Type"
                        selectedValue={slot.recurrenceType}
                        onChangeValue={(name, value) =>
                          updateTimeSlot(slot.id, 'recurrenceType', value)
                        }
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.recurrenceType}
                        errorMessage={timeSlotErrors[slot.id]?.recurrenceType}
                      />

                      <CustomInput
                        inputLabel="Price Multiplier"
                        name={`multiplier-${slot.id}`}
                        type="number"
                        value={slot.priceMultiplier}
                        placeholder="1.0"
                        onChangeValue={(name, value) =>
                          updateTimeSlot(slot.id, 'priceMultiplier', String(value))
                        }
                        decimalLimit={2}
                        maxValue={10}
                        valueRequired
                        error={!!timeSlotErrors[slot.id]?.priceMultiplier}
                        errorMessage={timeSlotErrors[slot.id]?.priceMultiplier}
                      />
                      <div
                        className="delete-action-button"
                        role="presentation"
                        onClick={() => removeTimeSlot(slot.id)}>
                        <DeleteIcon width="16" height="16" />
                      </div>
                      {slot.recurrenceType === 'non-recurring' && (
                        <div className="time-slot-conditional-row non-recurring-row">
                          <CustomMultiDatePicker
                            inputLabel="Date"
                            name={`date-${slot.id}`}
                            value={slot.dates || []}
                            placeholder="DD/MM/YYYY"
                            validFromDate={tariffDetails.enforcementDate?.split(' ')[0] || ''}
                            validToDate={tariffDetails.expiryDate?.split(' ')[0] || ''}
                            onChangeValue={(name, value) => updateTimeSlot(slot.id, 'dates', value)}
                            valueRequired
                            error={!!timeSlotErrors[slot.id]?.dates}
                            errorMessage={timeSlotErrors[slot.id]?.dates}
                          />
                        </div>
                      )}
                      {slot.recurrenceType === 'weekly' && (
                        <div className="time-slot-conditional-row weekly-row">
                          <CheckBoxButtons
                            inputLabel="Days of Week"
                            name={`daysOfWeek-${slot.id}`}
                            list={DAYS_OF_WEEK}
                            value={slot.daysOfWeek}
                            onChangeValue={(name, values) =>
                              updateTimeSlot(slot.id, 'daysOfWeek', values as string[])
                            }
                            valueRequired
                            error={!!timeSlotErrors[slot.id]?.daysOfWeek}
                            errorMessage={timeSlotErrors[slot.id]?.daysOfWeek}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EditTariff;
